import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Mail } from 'lucide-react';
import { applicationService } from '@core/supabase/services/applicationService';
import { matchResultService } from '@core/supabase/services/matchResultService';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { Drawer } from '@shared/components/ui/Drawer';
import type { Application } from '@core/types';

type PreviewProps = {
  orgId: string;
  applicationId: string | null;
  /** Fallback row from list while detail loads */
  listApp: Application | null;
  /** When true, omit the primary “Open full record” link (ListPreviewDrawer footer provides it). */
  hideOpenFullLink?: boolean;
  onClose?: () => void;
};

function DrawerSkeleton() {
  return (
    <div className="space-y-4 p-6" aria-busy="true" aria-label="Loading details">
      <div className="h-6 w-48 animate-pulse rounded vscode-bg-tertiary" />
      <div className="h-4 w-full max-w-md animate-pulse rounded vscode-bg-tertiary" />
      <div className="h-24 w-full animate-pulse rounded-lg vscode-bg-tertiary" />
    </div>
  );
}

/** Inner preview body (queries + sections). Use inside `Drawer` or `ListPreviewDrawer`. */
export function ApplicationPipelinePreviewBody({
  orgId,
  applicationId,
  listApp,
  hideOpenFullLink = false,
  onClose,
}: PreviewProps) {
  const open = Boolean(applicationId);

  const detailQuery = useQuery({
    queryKey: applicationId ? (['applications', 'detail', applicationId] as const) : ['applications', 'drawer', '__none'],
    queryFn: () => applicationService.getById(applicationId!),
    enabled: open && Boolean(applicationId),
    staleTime: 15_000,
  });

  const app = detailQuery.data ?? listApp;
  const candidateId = app?.candidate_id;
  const jobId = app?.job_id;

  const matchQuery = useQuery({
    queryKey:
      open && candidateId && jobId ? ['homeMatchPair', orgId, candidateId, jobId] : ['homeMatchPair', '__none'],
    queryFn: async () => {
      const rows = await matchResultService.list(orgId, { candidateId: candidateId!, jobId: jobId! });
      return rows[0] ?? null;
    },
    enabled: open && Boolean(candidateId && jobId && orgId),
    staleTime: 30_000,
  });

  if (!open || !applicationId) return null;

  const loading = detailQuery.isPending && !listApp;
  const err = detailQuery.isError ? (detailQuery.error instanceof Error ? detailQuery.error.message : 'Could not load') : null;

  const match = matchQuery.data;
  const topSkills = (match?.matched_skills ?? []).slice(0, 3);
  const dim = match?.dimension_scores;
  const dimLine =
    dim != null
      ? [
          dim.skill_overlap != null ? `Skills ${Math.round(dim.skill_overlap)}` : null,
          dim.semantic_similarity != null ? `Semantic ${Math.round(dim.semantic_similarity)}` : null,
          dim.experience_alignment != null ? `Experience ${Math.round(dim.experience_alignment)}` : null,
        ]
          .filter(Boolean)
          .join(' · ')
      : null;

  return (
    <>
      {loading ? <DrawerSkeleton /> : null}

      {!loading && err ? (
        <div className="p-6">
          <p className="text-sm vscode-text-secondary">{err}</p>
          <button type="button" className="btn-secondary mt-3 text-sm" onClick={() => void detailQuery.refetch()}>
            Retry
          </button>
        </div>
      ) : null}

      {!loading && !err && !app ? <p className="p-6 text-sm vscode-text-tertiary">Application not found.</p> : null}

      {!loading && !err && app ? (
        <div className="space-y-6 p-6">
          <section className="rounded-lg border vscode-border p-4">
            <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">Candidate</p>
            <p className="mt-1 text-sm vscode-text-primary">{app.candidate?.full_name ?? '—'}</p>
            <p className="mt-1 text-xs vscode-text-secondary">{app.candidate?.email ?? '—'}</p>
            <p className="mt-2 text-xs vscode-text-tertiary">
              {[app.candidate?.current_title, app.candidate?.current_company].filter(Boolean).join(' · ') || '—'}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusBadge status={app.status} />
              <span className="text-xs vscode-text-tertiary">{app.job?.title ?? '—'}</span>
            </div>
          </section>

          <section className="rounded-lg border vscode-border p-4">
            <p className="text-xs font-semibold uppercase tracking-wider vscode-text-tertiary">Smart Match (top signals)</p>
            {matchQuery.isPending ? (
              <div className="mt-3 space-y-2" aria-busy="true">
                <div className="h-4 w-3/4 animate-pulse rounded vscode-bg-tertiary" />
                <div className="h-4 w-1/2 animate-pulse rounded vscode-bg-tertiary" />
              </div>
            ) : matchQuery.isError ? (
              <p className="mt-2 text-sm vscode-text-secondary">
                Could not load match scores.{' '}
                <button type="button" className="text-[var(--vscode-text-link)] underline" onClick={() => void matchQuery.refetch()}>
                  Retry
                </button>
              </p>
            ) : !match ? (
              <p className="mt-2 text-sm vscode-text-tertiary">No scored match row for this pair yet.</p>
            ) : (
              <ul className="mt-3 list-inside list-disc space-y-2 text-sm vscode-text-secondary">
                <li>
                  <span className="font-medium vscode-text-primary">Skills matched: </span>
                  {topSkills.length ? topSkills.join(', ') : '—'}
                </li>
                <li>
                  <span className="font-medium vscode-text-primary">Note: </span>
                  {(match.recommendation ?? '').trim().slice(0, 160) || '—'}
                  {(match.recommendation ?? '').length > 160 ? '…' : ''}
                </li>
                <li>
                  <span className="font-medium vscode-text-primary">Dimensions: </span>
                  {dimLine || '—'}
                  {match.composite_score != null ? ` · Score ${Math.round(match.composite_score)}` : ''}
                </li>
              </ul>
            )}
          </section>

          <div className="flex flex-wrap gap-2">
            {!hideOpenFullLink ? (
              <Link
                to={`/applications/${app.id}`}
                className="btn-secondary inline-flex items-center gap-1.5 text-sm"
                onClick={onClose}
              >
                <ExternalLink className="h-4 w-4" />
                Open full record
              </Link>
            ) : null}
            <Link
              to={`/emails/compose?applicationId=${encodeURIComponent(app.id)}&returnTo=${encodeURIComponent('/applications')}`}
              className="btn-secondary inline-flex items-center gap-1.5 text-sm"
              onClick={onClose}
            >
              <Mail className="h-4 w-4" />
              Send email
            </Link>
            <Link to="/smart-match" className="btn-secondary text-sm" onClick={onClose}>
              Smart Match workspace
            </Link>
          </div>
        </div>
      ) : null}
    </>
  );
}

type Props = {
  orgId: string;
  applicationId: string | null;
  listApp: Application | null;
  onClose: () => void;
};

/** Standalone pipeline drawer (same behaviour as before §2.14; uses shared `Drawer` shell). */
export function ApplicationPipelineDrawer({ orgId, applicationId, listApp, onClose }: Props) {
  const open = Boolean(applicationId);
  const title = listApp?.candidate?.full_name ?? 'Application';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      titleId="pipeline-drawer-title"
      title={title}
      widthClass="w-[min(100vw,60vw)] min-w-[320px] max-w-[100vw]"
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
        <ApplicationPipelinePreviewBody
          orgId={orgId}
          applicationId={applicationId}
          listApp={listApp}
          hideOpenFullLink={false}
          onClose={onClose}
        />
      </div>
    </Drawer>
  );
}
